import {css, StyleSheet} from 'aphrodite';
import {noop} from 'jquery';
import React, {useEffect} from 'react';
import {compose} from 'redux';

import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Registry} from 'constants/RegistryConstants';
import {Grid, isAccessibleGridCell, isFullyAccessibleGrid} from 'constants/SampleConstants';
import {SchemaTag} from 'folder-items/FileConstants';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import HelpButton from 'help/HelpButton';
import NavStore from 'nav/NavStore';
import useGridContentAccessibility from 'samples/grids/hooks/useGridContentAccessibility';
import {DefineExistingPlateState} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {PLATE_MAP_MAX_GRID_HEIGHT, PLATE_MAP_MAX_GRID_WIDTH} from 'samples/plate-maps/PlateMapConstants';
import {isFixedPlate, isGridSchemaTooBigForNewPlateFeatures} from 'samples/SampleUtil';
import * as StorableActions from 'samples/StorableActions';
import ParentStorableDetails from 'samples/storables/ParentStorableDetails';
import {PlateSchema} from 'samples/storables/StorableSchemaConstants';
import StorableSearchInput from 'samples/storables/StorableSearchInput';
import {StorableSourceFormGroup} from 'samples/storables/StorableSourceSelector';
import StorableStore from 'samples/StorableStore';
import SchemaTagInputs from 'schemas/tags/SchemaTagInputs';
import {tagHasValue} from 'schemas/tags/TagUtil';
import {Filter, FilterKey, Operator} from 'search/SearchConstants';
import EditableStudiesList from 'studies/studyable/EditableStudiesList';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import {RED} from 'ui-library/Colors';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {hasSolidUnits} from '../PlateMapUtil';

export type DefineExistingPlateProps = BaseDefineExistingPlateProps & ConnectedProps;

interface ConnectedProps {
  selectedGrid: Grid | null;
  registries: Array<Registry>;
}

export interface BaseDefineExistingPlateProps {
  existingPlateState: DefineExistingPlateState;
  setExistingPlateState: (state: DefineExistingPlateState) => void;
  plateConstraintProps:
    | {
        requireEmptyPlate: false;
        plateSchema: null;
      }
    | {
        requireEmptyPlate: true;
        plateSchema: PlateSchema;
      };
}

/**
 * Whether a well plate has any annotations (non-default schema tags or well roles). Exported for testing.
 */
export function hasAnyAnnotations(grid: Grid): boolean {
  for (const cellRow of grid.cells) {
    for (const cell of cellRow) {
      if (isAccessibleGridCell(cell)) {
        if (cell.container.role != null) {
          return true;
        }
        for (const tag of cell.container.tags) {
          if (tagHasValue(tag)) {
            return true;
          }
        }
      }
    }
  }
  return false;
}

function getPlateError({
  grid,
  contentsQueryFailed,
  inaccessibleContainedItemIds,
  requireEmptyPlate,
}: {
  grid: Grid;
  contentsQueryFailed: boolean;
  inaccessibleContainedItemIds: Array<string>;
  requireEmptyPlate: boolean;
}): string | null {
  if (!grid.permissions.writable) {
    return 'You do not have permission to edit this plate';
  } else if (!isFixedPlate(grid)) {
    return 'The plate map tool does not support matrix plates';
  } else if (isGridSchemaTooBigForNewPlateFeatures(grid.storableSchema)) {
    return `The plate map tool does not support plates larger than ${PLATE_MAP_MAX_GRID_HEIGHT} wells by ${PLATE_MAP_MAX_GRID_WIDTH} wells`;
  } else if (hasSolidUnits(grid)) {
    return 'The plate map tool does not support plates that contain solid contents';
  } else if (inaccessibleContainedItemIds.length > 0) {
    return 'The plate map tool does not support plates that contain inaccessible contents';
  } else if (requireEmptyPlate && hasAnyAnnotations(grid)) {
    return 'Plate designs can only be used to edit plates without values set on wells';
  } else if (contentsQueryFailed) {
    return 'Unable to validate plate, please try again later.';
  }
  return null;
}

/** Contents of define plate step to select an existing well plate to edit */
function DefineExistingPlate({
  existingPlateState,
  setExistingPlateState,
  selectedGrid,
  registries,
  plateConstraintProps,
}: DefineExistingPlateProps): JSX.Element {
  function onSelectGrid(plates: Array<{id: string}>): void {
    const selectedGridId = plates.length > 0 ? plates[0].id : null;
    setExistingPlateState({
      selectedGridId,
      isValidating: selectedGridId != null,
      error: null,
      tags: [],
      appendStudyIds: existingPlateState.appendStudyIds,
    });
  }

  function getRegistryText(): string {
    if (selectedGrid == null) {
      return 'None';
    }
    return registries.find((reg) => reg.id === selectedGrid.storableSchema.registryId)?.name ?? 'None';
  }

  function handleUpdateTags(tags: Array<SchemaTag>): void {
    setExistingPlateState({...existingPlateState, tags});
  }

  const {status: contentsQueryStatus, inaccessibleContainedItemIds} = useGridContentAccessibility(
    isFullyAccessibleGrid(selectedGrid) ? selectedGrid : null
  );

  useEffect(() => {
    if (selectedGrid == null && existingPlateState.selectedGridId != null) {
      StorableActions.fetchGrid(existingPlateState.selectedGridId);
    }
  }, [existingPlateState.selectedGridId, selectedGrid]);

  useEffect(() => {
    if (selectedGrid != null) {
      if (existingPlateState.isValidating && contentsQueryStatus !== QueryResultType.PENDING) {
        setExistingPlateState({
          ...existingPlateState,
          selectedGridId: selectedGrid.id,
          isValidating: false,
          error: getPlateError({
            grid: selectedGrid,
            contentsQueryFailed: contentsQueryStatus === QueryResultType.ERROR,
            inaccessibleContainedItemIds,
            requireEmptyPlate: plateConstraintProps.requireEmptyPlate,
          }),
          tags: selectedGrid.tags,
        });
      } else if (existingPlateState.tags.length === 0 && selectedGrid.tags.length > 0) {
        // Tags may be updated after the initial plate is fetched
        setExistingPlateState({...existingPlateState, tags: selectedGrid.tags});
      }
    }
  }, [
    contentsQueryStatus,
    existingPlateState,
    inaccessibleContainedItemIds,
    plateConstraintProps.requireEmptyPlate,
    selectedGrid,
    setExistingPlateState,
  ]);

  const additionalFilters: Array<Filter> = [];
  if (plateConstraintProps.requireEmptyPlate && plateConstraintProps.plateSchema != null) {
    additionalFilters.push({
      key: FilterKey.GRID_EMPTY_CONTAINERS,
      operator: Operator.EQ,
      value: plateConstraintProps.plateSchema.width * plateConstraintProps.plateSchema.height,
    });
  }

  let schemaDisplay: string;
  if (plateConstraintProps.plateSchema != null) {
    schemaDisplay = plateConstraintProps.plateSchema.name;
  } else if (selectedGrid != null) {
    schemaDisplay = selectedGrid.storableSchema.name;
  } else {
    schemaDisplay = 'None';
  }

  return (
    <div {...getTestProps({component: 'DefineExistingPlate', element: 'root'})}>
      <div className={css(styles.fieldRow)}>
        <div className={css(styles.field)}>
          <FieldLabel label="Search for plate" />
          <StorableSearchInput
            additionalFilters={additionalFilters}
            autoFocus={false}
            onChange={onSelectGrid}
            registryId={null}
            selectedStorables={
              existingPlateState.selectedGridId ? [{id: existingPlateState.selectedGridId}] : []
            }
            shouldAllowMultiple={false}
            storableTypes={[ModelTypeConstants.PLATE]}
            {...(plateConstraintProps.plateSchema != null
              ? {storableSchemaId: plateConstraintProps.plateSchema.id}
              : {})}
          />
          {existingPlateState.error != null && (
            <p className={css(styles.errorMessage)}>{existingPlateState.error}</p>
          )}
        </div>
        <div className={css(styles.field)}>
          <FieldLabel label="Schema" />
          {plateConstraintProps.plateSchema != null && (
            <HelpButton
              renderContent={() =>
                'Plate records use the schema of the plate design from which they were created'
              }
              iconClassName={css(styles.helpButton)}
            />
          )}
          <p>{schemaDisplay}</p>
        </div>
      </div>
      <div className={css(styles.fieldRow, styles.textRow)}>
        <div className={css(styles.field)}>
          <FieldLabel label="Barcode" />
          <p>{selectedGrid?.barcode ?? 'None'}</p>
        </div>
        <div className={css(styles.field)}>
          <FieldLabel label="Registry" />
          <p>{getRegistryText()}</p>
        </div>
      </div>
      <div className={css(styles.fieldRow, styles.parentFieldRow)}>
        <div className={css(styles.field)}>
          <FieldLabel label="Location" />
          <ParentStorableDetails
            isWritable={false}
            onSetParentStorable={noop}
            parentStorable={
              selectedGrid?.parentStorableId == null
                ? null
                : StorableStore.getStorableSummary(selectedGrid.parentStorableId)
            }
          />
        </div>
        <div className={css(styles.field)}>
          <StorableSourceFormGroup
            onStorableChange={noop}
            registryId={null}
            selectedSourceId={selectedGrid?.source.id ?? null}
            isWritable={false}
          />
        </div>
      </div>

      {BenchlingConfig.STUDIES_ENABLE && selectedGrid != null && isItemStudyable(selectedGrid) && (
        <div className={css(styles.studiesField)}>
          <FieldLabel label="Studies" />
          <EditableStudiesList
            studyable={selectedGrid}
            isWritable={false}
            previewStudyIds={existingPlateState.appendStudyIds}
          />
        </div>
      )}
      {selectedGrid != null && existingPlateState.tags.length > 0 && (
        <div>
          <hr className={css(styles.schemaTagTopDivider)} />
          <FieldLabel label="Schema fields" />
          <SchemaTagInputs
            itemLinkAllowed={false}
            isCreating={true}
            onUpdateTags={handleUpdateTags}
            tags={existingPlateState.tags}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  field: {
    flex: 1,
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '16px',
  },
  textRow: {
    marginBottom: '10px',
  },
  parentFieldRow: {
    marginBottom: 0,
  },
  studiesField: {
    marginBottom: '16px',
  },
  schemaTagTopDivider: {
    marginTop: 0,
    marginBottom: '16px',
  },
  errorMessage: {
    color: RED,
  },
  helpButton: {
    marginLeft: '6px',
  },
});

function mapStorableStoreProps(
  store: typeof StorableStore,
  props: BaseDefineExistingPlateProps
): Partial<ConnectedProps> {
  return {
    selectedGrid:
      props.existingPlateState.selectedGridId == null
        ? null
        : store.getGrid(props.existingPlateState.selectedGridId),
  };
}

function mapNavStoreProps(store: typeof NavStore): Partial<ConnectedProps> {
  return {
    registries: store.getRegistries(),
  };
}

export default compose(
  StorableStore.connect(mapStorableStoreProps),
  NavStore.connect(mapNavStoreProps)
)(DefineExistingPlate);
